// @ts-strict-ignore
import { IspInspectionIdStoreSelectors } from './isp-inspection-id-store.selectors';
import {
  computeWorkflowEntryCountAutoFillUpdates,
  findQuestionInWorkflowAction,
  shouldDispatchAutoFillUpdate,
} from '@library/functions/checklist-auto-fill-condition/checklist-auto-fill-runtime.utils';
import { EQpHttpStatusCode } from '@library/models/qp-http.models';
import { EQpImageElementType } from '@library/models/qp-image.models';
import { EQpQuestionType } from '@library/models/qp-question.models';
import { IQpTestsChecklistElement } from '@library/models/qp-tests-checklist-element.models';
import { QpLoggerService } from '@library/services/qp-logger/qp-logger.service';
import { QpNotificationBarService } from '@library/services/qp-notification-bar/qp-notification-bar.service';
import { CommonRoutes } from '@one/app/common-routes';
import { ActionsQueueSyncService } from '@one/app/components/main/services/actions-queue-sync.service';
import { IEntity, IEntityConsultation } from '@one/app/pages/brd/shared/models/entity.model';
import { IspRoutes } from '@one/app/pages/isp/isp-routes';
import { IDefectsChecklistDefect } from '@one/app/pages/isp/pages/inspection/pages/id/pages/defects-checklist/isp-inspection-id-defect.models';
import { FindingsSummaryReviewSyncService } from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/findings-summary-review-sync.service';
import { IspInspectionIdWorkflowService } from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/isp-inspection-id-workflow.service';
import { SimplifiedMeasurementSyncService } from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/simplified-measurement-sync.service';
import {
  markInspectionAsAborted,
  markInspectionAsAbortedSuccess,
} from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/store/isp-inspection-id-store.actions';
import {
  ADD_CUSTOM_MEASUREMENT_PRODUCT_SAMPLE_SUCCESS,
  ADD_INSPECTION_PICTURE_SUCCESS,
  ADD_SITE_LOCATION_SUCCESS,
  addCustomMeasurementProductSample,
  addInspectionPictureSuccess,
  addSiteLocation,
  addTestEntry,
  ADD_TEST_ENTRY_SUCCESS,
  clearSiteLocation,
  COMMENT_TEST_SUCCESS,
  commentTest,
  deleteTestEntry,
  DELETE_TEST_ENTRY_SUCCESS,
  COMPLETE_DEFECTS_CHECKLIST_SUCCESS,
  COMPLETE_REFERENCE_SAMPLE_SUCCESS,
  COMPLETE_SAMPLE_COLLECTION_SUCCESS,
  COMPLETE_STARTING_PICTURES_SUCCESS,
  completeDefectsChecklist,
  completeReferenceSample,
  completeSampleCollection,
  completeStartingPictures,
  confirmFindingsSummaryReview,
  CREATE_DEFECT_SUCCESS,
  createDefect,
  createPictureTag,
  createPictureTagSuccess,
  DELETE_INSPECTION_IMAGE_SUCCESS,
  DELETE_TEST_CHECKLIST_TABLE_ROW_SUCCESS,
  deleteInspectionImage,
  deletePictureTag,
  deletePictureTagSuccess,
  deleteTestChecklistTableRow,
  ICompleteDefectsChecklistProps,
  ICompleteStartingPicturesProps,
  IDeleteWorkflowImageSuccess,
  ISaveRecipientsActionError,
  ISaveRecipientsActionSuccess,
  ISaveRecipientsActionType,
  IUpdateDefectCommentProps,
  IUpdateDefectProps,
  IUpdateProductActualQuantitiesProps,
  LOADED_SUCCESS_WORKFLOW,
  loadWorkflow,
  MARK_AS_NOT_APPLICABLE_SUCCESS,
  markAsNotApplicable,
  SAVE_CHECK_SUCCESS,
  SAVE_MEASUREMENT_SUCCESS,
  SAVE_RECIPIENTS_ERROR,
  SAVE_RECIPIENTS_SUCCESS,
  saveCheck,
  saveMeasurement,
  saveNullableTestAnswer,
  flushPendingNullableTestAnswers,
  saveProductPicture,
  saveProductPictureSuccess,
  saveRecipients,
  saveSimplifiedMeasures,
  saveSimplifiedMeasuresComment,
  saveSimplifiedMeasuresCommentSuccess,
  saveSimplifiedMeasuresError,
  saveSimplifiedMeasuresMarkedAsNotApplicable,
  saveTestAnswer,
  saveTestAnswerSuccess,
  saveTestTableRows,
  saveWorkflowActionCustomFieldsValue,
  saveWorkflowActionCustomFieldsValueSuccess,
  UPDATE_DEFECT_COMMENT_SUCCESS,
  UPDATE_DEFECT_SUCCESS,
  UPDATE_GLOBAL_REMARKS_SUCCESS,
  updateAvailableCartons,
  updateDefect,
  updateDefectComment,
  updateDefectCommentSuccess,
  updateGlobalRemarks,
  updateIsInspectionMarkedAsPending,
  updateIsInspectionMarkedAsPendingSuccess,
  updateIsWorkmanshipMarkedAsNA,
  updateIsWorkmanshipMarkedAsNASuccess,
  updatePictureTag,
  updatePictureTagSuccess,
  updateProductActualQuantities,
  updateProductActualQuantitiesError,
  updateProductActualQuantitiesSuccess,
  updateSimplifiedMeasurementSampling,
  updateSimplifiedMeasurementSamplingModificationReason,
  updateSimplifiedMeasurementSamplingModificationReasonSuccess,
  updateSimplifiedMeasurementSamplingSuccess,
  updateStartingCoordinates,
  updateStartingCoordinatesSuccess,
  updateTestTableRow,
} from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/store/isp-inspection-id-workflow-store.actions';
import { TestChecklistAnswerSyncService } from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/test-checklist-answer-sync.service';
import WorkflowUtils from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/workflow.utils';
import { getInspectionIdOrUuid } from '@one/app/pages/isp/shared/functions/isp-get-inspection-id-or-uuid';
import { isPictureSuccessForOpenInspection } from '@one/app/pages/isp/shared/functions/isp-inspection-picture-store.util';
import { IspInspectionPreloadService } from '@one/app/pages/isp/shared/services/isp-inspection-preload.service';
import { HTTP_SKIP_409_OPTIONS } from '@one/app/shared/constants/http-skip-409-options';
import { Answer } from '@one/app/shared/models/answer/answer.models';
import { IChecklistComment } from '@one/app/shared/models/inspection-consultation/inspection-consultation.models';
import { ERouteType } from '@one/app/shared/models/routes/route-type.models';
import {
  EWorkflowActionType,
  IWorkflow,
  IWorkflowAction,
  WorkflowAuditCheckType,
  WorkflowCheckType,
} from '@one/app/shared/models/workflow/workflow.models';
import { AccountService } from '@one/app/shared/services/account/account.service';
import { DatabaseService } from '@one/app/shared/services/database/database-inspection.service';
import { InspectionService } from '@one/app/shared/services/inspection/inspection.service';
import { ProductMeasurementsService } from '@one/app/shared/services/measurements/product-measurements.service';
import { IStoreState } from '@one/app/store/store.models';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { QimaOptionalType } from '@qima/ngx-qima';
import { cloneDeep, isNil } from 'lodash/index';
import { EMPTY, filter, from, Observable, of } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, map, mapTo, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

/**
 * @description
 * Not provided in root on purpose
 */
@Injectable()
export class IspInspectionIdWorkflowStoreEffects {
  public updateAvailableCartons$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(updateAvailableCartons),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<number> =>
            this._inspectionService.saveCartonsQuantity$(
              getInspectionIdOrUuid(storeState.inspectionStore.inspection),
              action.cartonsToPick,
              action.availableCartons,
              action.calculationMethod
            )
        ),
        mergeMap((): Observable<never> => EMPTY)
      ),
    { dispatch: false }
  );

  public loadWorkflow$ = createEffect(
    (): Observable<{
      type: '[Workflow] Load workflow success';
      workflow: QimaOptionalType<IWorkflow>;
    }> =>
      this._actions.pipe(
        ofType(loadWorkflow),
        mergeMap(
          (
            action
          ): Observable<{
            type: '[Workflow] Load workflow success';
            workflow: QimaOptionalType<IWorkflow>;
          }> => {
            let workflow$: Observable<IWorkflow>;

            if (typeof action.id === 'number') {
              workflow$ = this._inspectionService.getWorkflow$(action.id, false, HTTP_SKIP_409_OPTIONS);
            } else if (typeof action.id === 'string') {
              workflow$ = this._inspectionService.getWorkflow$(action.id, false);
            }

            return workflow$.pipe(
              filter((workflow): boolean => !!workflow),
              map(
                (
                  workflow: QimaOptionalType<IWorkflow>
                ): {
                  type: '[Workflow] Load workflow success';
                  workflow: QimaOptionalType<IWorkflow>;
                } => {
                  return {
                    type: LOADED_SUCCESS_WORKFLOW,
                    workflow,
                  };
                }
              ),
              catchError((error: Error | HttpErrorResponse): Observable<never> => {
                // Since it can throw HTTP error
                // When the inspector doesn't have the right to access the inspection
                // We navigate to a dedicated error page which is a bit better than the default forbidden page
                if (error instanceof HttpErrorResponse) {
                  if (error.status === EQpHttpStatusCode.FORBIDDEN) {
                    void this._router.navigate([IspRoutes.inspectionConsultationErrorForbidden()]);
                  } else if (error.status === EQpHttpStatusCode.GONE) {
                    void this._router.navigate([CommonRoutes.errorDeleted()], { queryParams: { type: ERouteType.INSPECTION } });
                  } else {
                    // @todo handle with better dedicated screens the errors
                    void this._router.navigate([IspRoutes.inspectionConsultationErrorUnknown()]);
                  }
                }

                return EMPTY;
              })
            );
          }
        )
      )
  );

  /**
   * Preload instruction files when workflow is loaded
   * This ensures test/defect files are cached with metadata for offline use
   */
  public preloadInstructionFilesOnWorkflowLoad$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(LOADED_SUCCESS_WORKFLOW),
        withLatestFrom(this._store),
        mergeMap(([action, storeState]): Observable<never> => {
          const workflow = (action as { type: string; workflow: IWorkflow }).workflow;
          const inspection = storeState.inspectionStore.inspection;

          if (workflow && inspection) {
            const inspectionId = getInspectionIdOrUuid(inspection);

            this._ispInspectionPreloadService.preloadDocuments(workflow, inspectionId);
          }

          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  public updateGlobalRemarks = createEffect(
    (): Observable<{ type: string; remarks: IChecklistComment }> =>
      this._actions.pipe(
        ofType(updateGlobalRemarks),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<{ type: string; remarks: IChecklistComment }> =>
            this._inspectionService
              .updateInspectionComment$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), action.remarks)
              .pipe(map((): { type: string; remarks: IChecklistComment } => action))
        ),
        map((action): { type: string; remarks: IChecklistComment } => {
          return {
            type: UPDATE_GLOBAL_REMARKS_SUCCESS,
            remarks: action.remarks,
          };
        })
      )
  );

  public updateIsInspectionMarkedAsPending = createEffect(
    (): Observable<{ type: string; isInspectionMarkedAsPending: boolean }> =>
      this._actions.pipe(
        ofType(updateIsInspectionMarkedAsPending),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<{ type: string; isInspectionMarkedAsPending: boolean }> =>
            this._inspectionService
              .updateInspectionIsMarkedAsPending$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                action.isInspectionMarkedAsPending
              )
              .pipe(map((): { type: string; isInspectionMarkedAsPending: boolean } => action))
        ),
        map(({ isInspectionMarkedAsPending }): { type: string; isInspectionMarkedAsPending: boolean } =>
          updateIsInspectionMarkedAsPendingSuccess({ isInspectionMarkedAsPending })
        )
      )
  );

  public updateIsWorkmanshipMarkedAsNA = createEffect(
    (): Observable<{ type: string; isWorkmanshipMarkedAsNA: boolean }> =>
      this._actions.pipe(
        ofType(updateIsWorkmanshipMarkedAsNA),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<{ type: string; isWorkmanshipMarkedAsNA: boolean }> =>
            this._inspectionService
              .updateInspectionWorkmanshipNA$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), action.isWorkmanshipMarkedAsNA)
              .pipe(map((): { type: string; isWorkmanshipMarkedAsNA: boolean } => action))
        ),
        map(({ isWorkmanshipMarkedAsNA }): { type: string; isWorkmanshipMarkedAsNA: boolean } =>
          updateIsWorkmanshipMarkedAsNASuccess({ isWorkmanshipMarkedAsNA })
        )
      )
  );

  public updateAbortInspection = createEffect(
    (): Observable<{ type: string; isAborted: boolean }> =>
      this._actions.pipe(
        ofType(markInspectionAsAborted),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<{ type: string; isAborted: boolean }> =>
            this._inspectionService
              .abortInspection$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), action.isAborted)
              .pipe(map((): { type: string; isAborted: boolean } => action))
        ),
        map(({ isAborted }): { type: string; isAborted: boolean } => {
          this._qpNotificationBarService.success(`inspection.abort.${isAborted ? 'success' : 'undo'}`);

          return markInspectionAsAbortedSuccess({ isAborted });
        })
      )
  );

  public resetIsInspectionMarkedAsPendingAfterAbortingInspection = createEffect(
    (): Observable<{ type: string; isInspectionMarkedAsPending: boolean }> =>
      this._actions.pipe(
        ofType(markInspectionAsAborted),
        withLatestFrom(this._store),
        filter(([{ isAborted }, storeState]): boolean => {
          const summaryReviewWorkflowAction: QimaOptionalType<IWorkflowAction> = WorkflowUtils.getWorkflowActionWithType(
            storeState.inspectionStore.workflow,
            EWorkflowActionType.FINDINGS_SUMMARY_REVIEW
          );

          if (!summaryReviewWorkflowAction) {
            return false;
          }

          return (
            isAborted &&
            summaryReviewWorkflowAction?.canInspectionBeMarkedAsPending &&
            summaryReviewWorkflowAction?.isInspectionMarkedAsPending
          );
        }),
        map((): { type: string; isInspectionMarkedAsPending: boolean } =>
          updateIsInspectionMarkedAsPending({ isInspectionMarkedAsPending: false })
        )
      )
  );

  // Test checklist 🧪

  public saveTestAnswer$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(saveTestAnswer, saveNullableTestAnswer),
        withLatestFrom(this._store),
        concatMap(([action, storeState]): Observable<never> => {
          const inspectionId = getInspectionIdOrUuid(storeState.inspectionStore.inspection);
          const elementType =
            'elementType' in action && action.elementType
              ? action.elementType
              : WorkflowUtils.getTestByIdInEntry(storeState.inspectionStore.workflow, action.id, action.entryUuid)?.type;

          if (!elementType) {
            this._qpLoggerService.error('Cannot persist test checklist answer: element type not found', {
              answerId: action.id,
              entryUuid: action.entryUuid,
            });

            return EMPTY;
          }

          return from(
            this._testChecklistAnswerSyncService.commitAndScheduleAnswerSync(
              inspectionId,
              action.id,
              action.value,
              elementType,
              action.entryUuid
            )
          ).pipe(
            catchError((error: unknown): Observable<never> => {
              this._qpLoggerService.error('Failed to persist test checklist answer', error);

              return EMPTY;
            }),
            mergeMap((): Observable<never> => EMPTY)
          );
        })
      ),
    { dispatch: false }
  );

  public flushPendingTextAnswers$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(flushPendingNullableTestAnswers),
        mergeMap(
          (): Observable<never> =>
            from(this._testChecklistAnswerSyncService.flushPendingTextAnswerSync()).pipe(
              catchError((error: unknown): Observable<never> => {
                this._qpLoggerService.error('Failed to flush pending TEXT test checklist answers', error);

                return EMPTY;
              }),
              mergeMap((): Observable<never> => EMPTY)
            )
        )
      ),
    { dispatch: false }
  );

  public commentTest$ = createEffect(
    (): Observable<{ type: string; id: string; comment: IChecklistComment }> =>
      this._actions.pipe(
        ofType(commentTest),
        withLatestFrom(this._store),
        mergeMap(
          ([element, storeState]): Observable<{ type: string; id: string; comment: IChecklistComment }> =>
            this._ispInspectionIdWorkflowService
              .updateAnswerCommentToApi$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                element.id,
                element.comment,
                element.entryUuid
              )
              .pipe(
                map((): { type: string; id: string; comment: IChecklistComment; entryUuid?: string } => {
                  return {
                    type: COMMENT_TEST_SUCCESS,
                    id: element.id,
                    comment: element.comment,
                    entryUuid: element.entryUuid,
                  };
                })
              )
        )
      )
  );

  public markAsNotApplicable$ = createEffect(
    (): Observable<{ type: string; id: string; notApplicable: boolean }> =>
      this._actions.pipe(
        ofType(markAsNotApplicable),
        withLatestFrom(this._store),
        mergeMap(
          ([element, storeState]): Observable<{ type: string; id: string; notApplicable: boolean }> =>
            this._ispInspectionIdWorkflowService
              .markAsNotApplicable$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                element.id,
                element.notApplicable,
                element.entryUuid
              )
              .pipe(
                map((): { type: string; id: string; notApplicable: boolean; entryUuid?: string } => {
                  return {
                    type: MARK_AS_NOT_APPLICABLE_SUCCESS,
                    id: element.id,
                    notApplicable: element.notApplicable,
                    entryUuid: element.entryUuid,
                  };
                })
              )
        )
      )
  );

  public deleteTableRow$ = createEffect(
    (): Observable<{
      type: '[Workflow - Test checklist] Delete table row success';
      id: string;
      answerId: string;
      numberOfColumns: number;
    }> =>
      this._actions.pipe(
        ofType(deleteTestChecklistTableRow),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<{
            type: '[Workflow - Test checklist] Delete table row success';
            id: string;
            answerId: string;
            numberOfColumns: number;
          }> => {
            const element: QimaOptionalType<IQpTestsChecklistElement<Answer>> = WorkflowUtils.getTestById(
              storeState.inspectionStore.workflow,
              action.id
            );

            if (!storeState.inspectionStore.inspection) {
              return EMPTY;
            }

            if (element) {
              return this._ispInspectionIdWorkflowService
                .deleteTableRow$(
                  getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                  action.id,
                  action.answerId,
                  action.numberOfColumns
                )
                .pipe(
                  map(
                    (): {
                      type: '[Workflow - Test checklist] Delete table row success';
                      id: string;
                      answerId: string;
                      numberOfColumns: number;
                    } => {
                      return {
                        type: DELETE_TEST_CHECKLIST_TABLE_ROW_SUCCESS,
                        id: action.id,
                        answerId: action.answerId,
                        numberOfColumns: action.numberOfColumns,
                      };
                    }
                  )
                );
            }

            return EMPTY;
          }
        )
      )
  );

  // Persist table rows to local database when new rows are added (online or offline).
  // Cell PATCH handlers read workflow from Dexie; without this, online row adds exist only in NgRx and are lost on reload.
  // Merges with existing Dexie cell values so a concurrent first cell save is not wiped by an empty new-row upsert.
  public saveTestTableRowsToDatabase$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(saveTestTableRows),
        withLatestFrom(this._store),
        mergeMap(([action, storeState]): Observable<never> => {
          if (isNil(storeState.inspectionStore.inspection) || isNil(storeState.inspectionStore.workflow)) {
            return EMPTY;
          }

          return from(
            this._databaseService.persistTestTableRows(getInspectionIdOrUuid(storeState.inspectionStore.inspection), action.id, action.rows)
          ).pipe(
            catchError((error: unknown): Observable<never> => {
              this._qpLoggerService.error('Failed to persist table rows to database', error);

              return EMPTY;
            }),
            mergeMap((): Observable<never> => EMPTY)
          );
        })
      ),
    { dispatch: false }
  );

  /**
   * After a TABLE cell answer is applied to NgRx, persist the store workflow to Dexie.
   * Covers the offline first-fill / new-row race where Dexie lacked column ids at PATCH time
   * but the store already had the in-memory-initialized row structure and values.
   */
  public persistTestTableAnswerToDatabase$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(saveTestAnswerSuccess),
        withLatestFrom(this._store),
        mergeMap(([action, storeState]): Observable<never> => {
          if (isNil(storeState.inspectionStore.inspection) || isNil(storeState.inspectionStore.workflow)) {
            return EMPTY;
          }

          const element = WorkflowUtils.getTestById(storeState.inspectionStore.workflow, action.id);

          if (!element || element.type !== EQpQuestionType.TABLE) {
            return EMPTY;
          }

          return from(this._databaseService.persistWorkflowSnapshot(cloneDeep(storeState.inspectionStore.workflow))).pipe(
            catchError((error: unknown): Observable<never> => {
              this._qpLoggerService.error('Failed to persist table answer workflow snapshot to database', error);

              return EMPTY;
            }),
            mergeMap((): Observable<never> => EMPTY)
          );
        })
      ),
    { dispatch: false }
  );

  /**
   * Field diagnostic when a defect picture could not be attached to the store workflow (PL-30487).
   * Reducers stay pure; logging uses the same structured fields as post-sync re-attach / saveInspectionFile.
   */
  public logStaleDefectPictureAttachSkip$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(addInspectionPictureSuccess),
        filter(
          (action): boolean =>
            action.elementType === EQpImageElementType.DEFECTS && !!action.elementPathId && !!(action.data.imageId ?? action.newImageId)
        ),
        withLatestFrom(this._store),
        filter(([action, storeState]): boolean => {
          return isPictureSuccessForOpenInspection(storeState.inspectionStore?.inspection, action.inspectionId);
        }),
        tap(([action, storeState]): void => {
          const workflow = storeState.inspectionStore?.workflow;

          if (!workflow) {
            return;
          }

          const imageId = action.data.imageId ?? action.newImageId;

          if (WorkflowUtils.workflowContainsImageId(workflow, imageId)) {
            return;
          }

          let message = 'Image not present in store workflow after addInspectionPictureSuccess';

          try {
            WorkflowUtils.addDefectsPicture(workflow, action.elementPathId as string, imageId, action.data);
          } catch (error) {
            message = error instanceof Error ? error.message : String(error);
          }

          this._qpLoggerService.warn('[Workflow] Store defect picture attach skipped: local workflow is stale', {
            imageId,
            entity: EQpImageElementType.DEFECTS,
            entityId: action.elementPathId,
            message,
          });
        }),
        mergeMap((): Observable<never> => EMPTY)
      ),
    { dispatch: false }
  );

  // Multiple-entry test checklist 🧪🧪
  // Add/delete entry via HTTP so the inspection interceptor can persist locally and queue sync when offline.
  public addTestEntry$ = createEffect(
    (): Observable<{ type: string; id: string; entryUuid: string; createdAt: string; createdByInspectorId?: number }> =>
      this._actions.pipe(
        ofType(addTestEntry),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<{
            type: string;
            id: string;
            entryUuid: string;
            createdAt: string;
            createdByInspectorId?: number;
          }> => {
            if (isNil(storeState.inspectionStore.inspection) || isNil(storeState.inspectionStore.workflow)) {
              return EMPTY;
            }

            const successAction = {
              type: ADD_TEST_ENTRY_SUCCESS,
              id: action.id,
              entryUuid: action.entryUuid,
              createdAt: action.createdAt,
              createdByInspectorId: action.createdByInspectorId,
            };

            return this._ispInspectionIdWorkflowService
              .addEntry$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                action.id,
                action.entryUuid,
                action.createdAt,
                action.createdByInspectorId
              )
              .pipe(
                map((): { type: string; id: string; entryUuid: string; createdAt: string; createdByInspectorId?: number } => successAction)
              );
          }
        )
      )
  );

  public deleteTestEntry$ = createEffect(
    (): Observable<{ type: string; id: string; entryUuid: string }> =>
      this._actions.pipe(
        ofType(deleteTestEntry),
        withLatestFrom(this._store),
        mergeMap(([action, storeState]): Observable<{ type: string; id: string; entryUuid: string }> => {
          if (isNil(storeState.inspectionStore.inspection) || isNil(storeState.inspectionStore.workflow)) {
            return EMPTY;
          }

          const successAction = {
            type: DELETE_TEST_ENTRY_SUCCESS,
            id: action.id,
            entryUuid: action.entryUuid,
          };

          return this._ispInspectionIdWorkflowService
            .deleteEntry$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), action.id, action.entryUuid)
            .pipe(map((): { type: string; id: string; entryUuid: string } => successAction));
        })
      )
  );

  // Updating table rows cant have debounce on effect as user can add multiple rows quickly one after another
  public updateTableRow$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(updateTestTableRow),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<never> =>
            from(
              this._testChecklistAnswerSyncService.commitAndScheduleAnswerSync(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                action.id,
                action.value,
                action.elementType
              )
            ).pipe(
              catchError((error: unknown): Observable<never> => {
                this._qpLoggerService.error('Failed to persist test table row answer', error);

                return EMPTY;
              }),
              mergeMap((): Observable<never> => EMPTY)
            )
        )
      ),
    { dispatch: false }
  );

  // Instruction 🧾

  public saveCheck$ = createEffect(
    (): Observable<{ type: string; actionId: string; check: WorkflowCheckType | WorkflowAuditCheckType }> =>
      this._actions.pipe(
        ofType(saveCheck),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]): Observable<{ type: string; actionId: string; check: WorkflowCheckType | WorkflowAuditCheckType }> =>
            this._ispInspectionIdWorkflowService
              .saveCheckToAPI$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), props.actionId, props.check)
              .pipe(
                map((): { type: string; actionId: string; check: WorkflowCheckType | WorkflowAuditCheckType } => {
                  return {
                    type: SAVE_CHECK_SUCCESS,
                    actionId: props.actionId,
                    check: props.check,
                  };
                })
              )
        )
      )
  );

  // Defect checklist ❌

  public createDefect$ = createEffect(
    (): Observable<{ type: string; defect: IDefectsChecklistDefect }> =>
      this._actions.pipe(
        ofType(createDefect),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]): Observable<{ type: string; defect: IDefectsChecklistDefect }> =>
            this._inspectionService
              .createDefect$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                props.name,
                props.defectType,
                props.stepId,
                props.classification,
                props.unit,
                props.purchaseOrderProductId,
                props.defectId,
                props.description,
                props.documents
              )
              .pipe(
                map((created): { type: string; defect: IDefectsChecklistDefect } => {
                  const defect: IDefectsChecklistDefect = {
                    ...created,
                    description: props.description ?? created.description,
                    documents: props.documents ?? created.documents,
                    translations: props.translations ?? created.translations,
                    // Preserve public defect id so attachment metadata/download use /api/public-defects/... not /api/defects-checklists/...
                    qspDefectId: props.defectId ?? created.qspDefectId,
                  };

                  return {
                    type: CREATE_DEFECT_SUCCESS,
                    defect,
                  };
                })
              )
        )
      )
  );

  public updateDefect$ = createEffect(
    (): Observable<IUpdateDefectProps & { type: string }> =>
      this._actions.pipe(
        ofType(updateDefect),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]): Observable<IUpdateDefectProps & { type: string }> =>
            this._inspectionService
              .updateDefect$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                props.defectId,
                props.quantity,
                props.classification,
                props.purchaseOrderProductId
              )
              .pipe(
                map((): IUpdateDefectProps & { type: string } => {
                  return {
                    type: UPDATE_DEFECT_SUCCESS,
                    defectId: props.defectId,
                    quantity: props.quantity,
                    classification: props.classification,
                    purchaseOrderProductId: props.purchaseOrderProductId,
                  };
                })
              )
        )
      )
  );

  public updateDefectComment$ = createEffect(
    (): Observable<IUpdateDefectCommentProps & { type: string }> =>
      this._actions.pipe(
        ofType(updateDefectComment),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]): Observable<IUpdateDefectCommentProps & { type: string }> =>
            this._inspectionService
              .updateDefectComment$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                props.defectId,
                props.comment,
                props.purchaseOrderProductId
              )
              .pipe(
                map((): IUpdateDefectCommentProps & { type: string } => {
                  return {
                    type: UPDATE_DEFECT_COMMENT_SUCCESS,
                    defectId: props.defectId,
                    comment: props.comment,
                    purchaseOrderProductId: props.purchaseOrderProductId,
                  };
                })
              )
        )
      )
  );

  /**
   * Unified effect to set default quantity of 0 when defect comment is updated or inspection picture is added
   * but the defect quantity is undefined/null/0. This ensures that defects always have a valid quantity in database.
   * When comments or images are added, and syncs locally created defects (with default quantity 0) to the API.
   * Big debounce added to avoid multiple rapid updates.
   */
  public setDefaultQuantityOnDefectInteraction$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(updateDefectCommentSuccess, addInspectionPictureSuccess),
        withLatestFrom(this._store),
        distinctUntilChanged(([action], [prevAction]): boolean => {
          const currentDefectId = this._getDefectIdFromAction(action);
          const prevDefectId = this._getDefectIdFromAction(prevAction);

          return currentDefectId === prevDefectId;
        }),
        filter(([action, storeState]): boolean => {
          if (!storeState.inspectionStore.workflow) {
            return false;
          }

          if (
            action.type === ADD_INSPECTION_PICTURE_SUCCESS &&
            !isPictureSuccessForOpenInspection(storeState.inspectionStore.inspection, action.inspectionId)
          ) {
            return false;
          }

          return this._shouldSetDefaultQuantity(action, storeState.inspectionStore.workflow);
        }),
        map(([action, storeState]): Action => this._createDefaultQuantityUpdateAction(action, storeState.inspectionStore.workflow))
      )
  );

  public completeDefectsChecklist$ = createEffect(
    (): Observable<{ type: string } & ICompleteDefectsChecklistProps> =>
      this._actions.pipe(
        ofType(completeDefectsChecklist),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]: [ICompleteDefectsChecklistProps, IStoreState]): Observable<
            {
              type: string;
            } & ICompleteDefectsChecklistProps
          > =>
            this._inspectionService
              .completeDefectsChecklist$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                props.stepId,
                props.actionId,
                props.purchaseOrderProductId
              )
              .pipe(
                map((): { type: string } & ICompleteDefectsChecklistProps => {
                  return {
                    type: COMPLETE_DEFECTS_CHECKLIST_SUCCESS,
                    stepId: props.stepId,
                    actionId: props.actionId,
                    purchaseOrderProductId: props.purchaseOrderProductId,
                  };
                })
              )
        )
      )
  );

  public completeStartingPictures$ = createEffect(
    (): Observable<{ type: string } & ICompleteStartingPicturesProps> =>
      this._actions.pipe(
        ofType(completeStartingPictures),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]: [ICompleteStartingPicturesProps, IStoreState]): Observable<
            {
              type: string;
            } & ICompleteStartingPicturesProps
          > =>
            this._inspectionService
              .completeStartingPictures$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), props.pathId)
              .pipe(
                map((): { type: string } & ICompleteStartingPicturesProps => {
                  return {
                    type: COMPLETE_STARTING_PICTURES_SUCCESS,
                    pathId: props.pathId,
                  };
                })
              )
        )
      )
  );

  // Recipients 📧

  public saveRecipients = createEffect(
    (): Observable<ISaveRecipientsActionType> =>
      this._actions.pipe(
        ofType(saveRecipients),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]): Observable<ISaveRecipientsActionType> =>
            this._inspectionService
              .updateInspectionRecipients$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), props.recipients)
              .pipe(
                map((): ISaveRecipientsActionSuccess => {
                  return {
                    type: SAVE_RECIPIENTS_SUCCESS,
                    actionId: props.actionId,
                    recipients: props.recipients,
                  };
                }),
                catchError((error: unknown): Observable<ISaveRecipientsActionError> => {
                  this._qpLoggerService.error(error);

                  return of({
                    type: SAVE_RECIPIENTS_ERROR,
                    error,
                  });
                })
              )
        )
      )
  );

  // Starting Coordinates 📍

  public updateStartingCoordinates$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(updateStartingCoordinates),
        withLatestFrom(this._store),
        mergeMap(
          ([action, storeState]): Observable<Action> =>
            this._inspectionService
              .updateStartingCoordinates$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), {
                latitude: action.latitude,
                longitude: action.longitude,
                isEnteredManually: action.isEnteredManually,
              })
              .pipe(
                map(
                  (): Action =>
                    updateStartingCoordinatesSuccess({
                      latitude: action.latitude,
                      longitude: action.longitude,
                      isEnteredManually: action.isEnteredManually,
                    })
                ),
                catchError((error: unknown): Observable<never> => {
                  this._qpLoggerService.error(error);

                  return EMPTY;
                })
              )
        )
      )
  );

  public updateProductActualQuantities$ = createEffect(
    (): Actions =>
      this._actions.pipe(
        ofType(updateProductActualQuantities),
        concatMap(
          (
            action
          ): Observable<{
            isApiCallInError: boolean;
            action: IUpdateProductActualQuantitiesProps;
            error?: string;
          }> => {
            return this._inspectionService
              .updateInspectionActualQuantities$(action.inspectionId, {
                productId: action.productId,
                purchaseOrderProductId: action.purchaseOrderProductId,
                produced: action.produced,
                packed: action.packed,
              })
              .pipe(
                map(
                  (): {
                    isApiCallInError: boolean;
                    action: IUpdateProductActualQuantitiesProps;
                    error?: string;
                  } => ({
                    isApiCallInError: false,
                    action,
                  })
                ),
                catchError(
                  (
                    error: string
                  ): Observable<{
                    isApiCallInError: boolean;
                    action: IUpdateProductActualQuantitiesProps;
                    error?: string;
                  }> =>
                    of({
                      isApiCallInError: true,
                      action,
                      error,
                    })
                )
              );
          }
        ),
        switchMap(({ isApiCallInError, action, error }): Action[] => {
          if (!isApiCallInError) {
            return [
              updateProductActualQuantitiesSuccess({
                productActualQuantities: {
                  productId: action.productId,
                  purchaseOrderProductId: action.purchaseOrderProductId,
                  produced: action.produced,
                  packed: action.packed,
                },
              }),
            ];
          }

          return [updateProductActualQuantitiesError({ error })];
        })
      )
  );

  // Site Location 📍

  public saveLocation = createEffect(
    (): Observable<{ type: string; entity: IEntity | IEntityConsultation; stepId: number; actionId: number }> =>
      this._actions.pipe(
        ofType(addSiteLocation, clearSiteLocation),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]): Observable<{
            type: string;
            entity: IEntity | IEntityConsultation;
            stepId: number;
            actionId: number;
          }> =>
            this._ispInspectionIdWorkflowService
              .saveLocation$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), +props.entity?.id)
              .pipe(
                map(
                  (): {
                    type: string;
                    entity: IEntity | IEntityConsultation;
                    stepId: number;
                    actionId: number;
                  } => {
                    return {
                      type: ADD_SITE_LOCATION_SUCCESS,
                      actionId: props.actionId,
                      entity: props.entity,
                      stepId: props.stepId,
                    };
                  }
                )
              )
        )
      )
  );

  // Simplified Measurements 📏

  public saveSimplifiedMeasures$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(saveSimplifiedMeasures),
        mergeMap(
          (action): Observable<Action> =>
            from(this._simplifiedMeasurementSyncService.commitAndScheduleMeasures(action.inspectionId, action.simplifiedMeasures)).pipe(
              mergeMap((): Observable<Action> => EMPTY),
              catchError((error: unknown): Observable<Action> => {
                this._qpLoggerService.error('Failed to save simplified measures locally', error);

                return of(saveSimplifiedMeasuresError({ error: this._formatLocalSyncError(error) }));
              })
            )
        )
      )
  );

  public saveSimplifiedMeasuresMarkedAsNotApplicable$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(saveSimplifiedMeasuresMarkedAsNotApplicable),
        mergeMap(
          (action): Observable<Action> =>
            from(
              this._simplifiedMeasurementSyncService.commitAndScheduleMarkedAsNotApplicable(action.inspectionId, {
                notApplicable: action.notApplicable,
              })
            ).pipe(
              mergeMap((): Observable<Action> => EMPTY),
              catchError((error: unknown): Observable<Action> => {
                this._qpLoggerService.error('Failed to save simplified measures N/A status locally', error);

                return of(saveSimplifiedMeasuresError({ error: this._formatLocalSyncError(error) }));
              })
            )
        )
      )
  );

  public saveSimplifiedMeasuresComment$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(saveSimplifiedMeasuresComment),
        mergeMap((action): Actions => {
          return this._inspectionService.saveSimplifiedMeasurementComment$(action.inspectionId, action.comment).pipe(
            map((): Action => saveSimplifiedMeasuresCommentSuccess({ comment: action.comment })),
            catchError((error): Observable<Action> => of(saveSimplifiedMeasuresError({ error })))
          );
        })
      )
  );

  public updateSimplifiedMeasurementSampling$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(updateSimplifiedMeasurementSampling),
        mergeMap((action): Actions => {
          return this._inspectionService.saveSimplifiedMeasurementSampling$(action.inspectionId, action.sampling).pipe(
            map((): Action => updateSimplifiedMeasurementSamplingSuccess({ inspectionId: action.inspectionId, sampling: action.sampling })),
            catchError((error): Observable<Action> => of(saveSimplifiedMeasuresError({ error })))
          );
        })
      )
  );

  public updateSimplifiedMeasurementSamplingModificationReason$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(updateSimplifiedMeasurementSamplingModificationReason),
        mergeMap((action): Actions => {
          return this._inspectionService.saveSimplifiedMeasurementSamplingModificationReason(action.inspectionId, action.reason).pipe(
            map(
              (): Action =>
                updateSimplifiedMeasurementSamplingModificationReasonSuccess({ inspectionId: action.inspectionId, reason: action.reason })
            ),
            catchError((error): Observable<Action> => of(saveSimplifiedMeasuresError({ error })))
          );
        })
      )
  );

  // Measurements 📏

  public saveMeasurement = createEffect(
    (): Observable<{ type: string; measurementId: string; measure: number; sectionName: QimaOptionalType<string> }> =>
      this._actions.pipe(
        ofType(saveMeasurement),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]): Observable<{
            type: string;
            measurementId: string;
            measure: number;
            sectionName: QimaOptionalType<string>;
          }> =>
            this._productMeasurementService
              .saveMeasure$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                props.measurementId,
                props.measure,
                props.sectionName
              )
              .pipe(
                map((): { type: string; measurementId: string; measure: number; sectionName: QimaOptionalType<string> } => {
                  return {
                    type: SAVE_MEASUREMENT_SUCCESS,
                    measurementId: props.measurementId,
                    measure: props.measure,
                    sectionName: props.sectionName,
                  };
                })
              )
        )
      )
  );

  public addCustomMeasurementProductSample$ = createEffect(
    (): Observable<{ type: string; path: string; sectionName: string }> =>
      this._actions.pipe(
        ofType(addCustomMeasurementProductSample),
        withLatestFrom(this._store),
        mergeMap(([props, storeState]): Observable<{ type: string; path: string; sectionName: string }> => {
          return this._productMeasurementService
            .addCustomMeasurementProductSample$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), props.path, props.sectionName)
            .pipe(
              map((): { type: string; path: string; sectionName: string } => {
                return {
                  type: ADD_CUSTOM_MEASUREMENT_PRODUCT_SAMPLE_SUCCESS,
                  path: props.path,
                  sectionName: props.sectionName,
                };
              })
            );
        })
      )
  );

  // Product picture 📸

  public saveProductPicture$ = createEffect(
    (): Observable<ReturnType<typeof saveProductPictureSuccess>> =>
      this._actions.pipe(
        ofType(saveProductPicture),
        withLatestFrom(this._store),
        mergeMap(
          ([props, storeState]): Observable<ReturnType<typeof saveProductPictureSuccess>> =>
            this._ispInspectionIdWorkflowService
              .saveProductPictureToApi$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), props.actionId, props.action)
              .pipe(
                map(
                  (): ReturnType<typeof saveProductPictureSuccess> =>
                    saveProductPictureSuccess({ actionId: props.actionId, action: props.action })
                )
              )
        )
      )
  );

  // Custom fields ✨

  public saveWorkflowActionCustomFieldsValue$ = createEffect(
    (): Actions =>
      this._actions.pipe(
        ofType(saveWorkflowActionCustomFieldsValue),
        withLatestFrom(this._store),
        switchMap(
          ([props, storeState]): Actions =>
            this._ispInspectionIdWorkflowService
              .saveCustomFieldValueToApi$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), props.fieldId, props.answer)
              .pipe(
                map((): Action => {
                  return saveWorkflowActionCustomFieldsValueSuccess(props);
                })
              )
        )
      )
  );

  // Findings summary review 📝

  public updateFindingsSummaryReview$ = createEffect(
    (): Observable<never> =>
      this._actions.pipe(
        ofType(confirmFindingsSummaryReview),
        withLatestFrom(this._store),
        mergeMap(
          ([_props, storeState]): Observable<never> =>
            from(
              this._findingsSummaryReviewSyncService.confirmAndSchedule(getInspectionIdOrUuid(storeState.inspectionStore.inspection))
            ).pipe(
              catchError((error: unknown): Observable<never> => {
                this._qpLoggerService.error('Failed to confirm findings summary review locally', error);

                return EMPTY;
              }),
              mergeMap((): Observable<never> => EMPTY)
            )
        )
      ),
    { dispatch: false }
  );

  // Images 📷

  public deleteWorkflowImage$: Observable<IDeleteWorkflowImageSuccess> = createEffect(
    (): Observable<IDeleteWorkflowImageSuccess> =>
      this._actions.pipe(
        ofType(deleteInspectionImage),
        tap(({ data }): void => {
          if (data.imageId) {
            this._actionsQueueSyncService.rememberUserDeletedImageId(data.imageId);
          }
        }),
        withLatestFrom(this._store),
        mergeMap(
          ([props]): Observable<IDeleteWorkflowImageSuccess> =>
            this._inspectionService.deleteInspectionImage$(props).pipe(
              tap((): void => {
                this._qpNotificationBarService.success('inspection.image-edition.delete-modal.notification.success.message');
              }),
              map((): IDeleteWorkflowImageSuccess => {
                return {
                  ...props,
                  type: DELETE_INSPECTION_IMAGE_SUCCESS,
                };
              })
            )
        )
      )
  );

  // Reference sample

  public completeReferenceSample$: Observable<{ type: string }> = createEffect(
    (): Observable<{ type: string }> =>
      this._actions.pipe(
        ofType(completeReferenceSample),
        withLatestFrom(this._store),
        mergeMap(
          ([_props, storeState]): Observable<{ type: string }> =>
            this._inspectionService
              .completeReferenceSample$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                _props.stepId,
                _props.actionId,
                _props.stepCompletionTime
              )
              .pipe(
                mapTo({
                  type: COMPLETE_REFERENCE_SAMPLE_SUCCESS,
                })
              )
        )
      )
  );

  // Sample collection

  public completeSampleCollection$: Observable<{ type: string }> = createEffect(
    (): Observable<{ type: string }> =>
      this._actions.pipe(
        ofType(completeSampleCollection),
        withLatestFrom(this._store),
        mergeMap(
          ([_props, storeState]): Observable<{ type: string }> =>
            this._inspectionService
              .completeSampleCollection$(
                getInspectionIdOrUuid(storeState.inspectionStore.inspection),
                _props.stepId,
                _props.actionId,
                _props.stepCompletionTime
              )
              .pipe(
                mapTo({
                  type: COMPLETE_SAMPLE_COLLECTION_SUCCESS,
                })
              )
        )
      )
  );

  // Picture tags

  public createPictureTag$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(createPictureTag),
        withLatestFrom(this._store),
        switchMap(([action, storeState]): Observable<Action> => {
          return this._inspectionService
            .createPictureTag$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), action.pictureTag)
            .pipe(
              map((): Action => {
                return createPictureTagSuccess({ pictureTag: action.pictureTag });
              })
            );
        })
      )
  );

  public updatePictureTag$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(updatePictureTag),
        withLatestFrom(this._store),
        switchMap(([action, storeState]): Observable<Action> => {
          return this._inspectionService
            .updatePictureTag$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), action.pictureTag)
            .pipe(
              map((): Action => {
                return updatePictureTagSuccess({ pictureTag: action.pictureTag });
              })
            );
        })
      )
  );

  public deletePictureTag$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(deletePictureTag),
        withLatestFrom(this._store),
        switchMap(([action, storeState]): Observable<Action> => {
          return this._inspectionService
            .deletePictureTag$(getInspectionIdOrUuid(storeState.inspectionStore.inspection), action.pictureTag)
            .pipe(
              map((): Action => {
                this._qpNotificationBarService.success('library.components.qp-picture-tags.tag-deleted');

                return deletePictureTagSuccess({ pictureTag: action.pictureTag });
              })
            );
        })
      )
  );

  public applyEntryCountAutoFill$ = createEffect(
    (): Observable<Action> =>
      this._actions.pipe(
        ofType(saveTestAnswerSuccess, ADD_TEST_ENTRY_SUCCESS, DELETE_TEST_ENTRY_SUCCESS),
        withLatestFrom(this._ispInspectionIdStoreSelectors.workflow$, this._accountService.getBrand$()),
        mergeMap(([, workflow, brand]): Observable<Action> => {
          const biotechEnabled = brand?.biotechEnabled === true;

          if (!biotechEnabled || !workflow) {
            return EMPTY;
          }

          const updates = computeWorkflowEntryCountAutoFillUpdates(workflow, true);
          const dispatchActions: Action[] = [];
          const processedQuestionIds = new Set<string>();

          workflow.steps?.forEach((step): void => {
            step.actions?.forEach((action): void => {
              if (action.type !== EWorkflowActionType.TESTS_CHECKLIST) {
                return;
              }

              updates.forEach((update): void => {
                if (processedQuestionIds.has(update.id)) {
                  return;
                }

                const question = findQuestionInWorkflowAction(action, update.id);

                if (!question || !shouldDispatchAutoFillUpdate(question, update)) {
                  return;
                }

                processedQuestionIds.add(update.id);

                if (update.value == null) {
                  dispatchActions.push(saveNullableTestAnswer({ id: update.id, value: null }));
                } else {
                  dispatchActions.push(
                    saveTestAnswer({
                      id: update.id,
                      value: update.value,
                      elementType: update.elementType,
                    })
                  );
                }
              });
            });
          });

          return dispatchActions.length > 0 ? from(dispatchActions) : EMPTY;
        })
      )
  );

  public constructor(
    private readonly _inspectionService: InspectionService,
    private readonly _actionsQueueSyncService: ActionsQueueSyncService,
    private readonly _ispInspectionIdWorkflowService: IspInspectionIdWorkflowService,
    private readonly _testChecklistAnswerSyncService: TestChecklistAnswerSyncService,
    private readonly _findingsSummaryReviewSyncService: FindingsSummaryReviewSyncService,
    private readonly _simplifiedMeasurementSyncService: SimplifiedMeasurementSyncService,
    private readonly _productMeasurementService: ProductMeasurementsService,
    private readonly _actions: Actions,
    private readonly _store: Store<IStoreState>,
    private readonly _router: Router,
    private readonly _qpLoggerService: QpLoggerService,
    private readonly _qpNotificationBarService: QpNotificationBarService,
    private readonly _ispInspectionIdStoreSelectors: IspInspectionIdStoreSelectors,
    private readonly _databaseService: DatabaseService,
    private readonly _ispInspectionPreloadService: IspInspectionPreloadService,
    private readonly _accountService: AccountService
  ) {}

  private _formatLocalSyncError(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }

  private _extractDefectInfoFromCommentAction(action: IUpdateDefectCommentProps & { type: string }): {
    defectId: string;
    purchaseOrderProductId: QimaOptionalType<number>;
  } {
    return {
      defectId: action.defectId,
      purchaseOrderProductId: action.purchaseOrderProductId,
    };
  }

  private _extractDefectInfoFromPictureAction(action: {
    data: unknown;
    elementType: QimaOptionalType<string>;
    elementPathId: QimaOptionalType<string>;
    newImageId: string;
    type: string;
  }): { defectId: string; purchaseOrderProductId: QimaOptionalType<number> } | null {
    // Only process defect images
    if (action.elementType !== EQpImageElementType.DEFECTS || !action.elementPathId) {
      return null;
    }

    return {
      defectId: action.elementPathId,
      purchaseOrderProductId: undefined, // Picture actions don't have this info
    };
  }

  private _extractDefectInfoFromAction(action: Action): {
    defectId: string;
    purchaseOrderProductId: QimaOptionalType<number>;
  } | null {
    if (action.type === UPDATE_DEFECT_COMMENT_SUCCESS) {
      return this._extractDefectInfoFromCommentAction(action as IUpdateDefectCommentProps & { type: string });
    }

    if (action.type === ADD_INSPECTION_PICTURE_SUCCESS) {
      return this._extractDefectInfoFromPictureAction(
        action as {
          data: unknown;
          elementType: QimaOptionalType<string>;
          elementPathId: QimaOptionalType<string>;
          newImageId: string;
          type: string;
        }
      );
    }

    return null;
  }

  private _findDefectFoundEntry(
    defect: IDefectsChecklistDefect,
    purchaseOrderProductId: QimaOptionalType<number>
  ): { quantity?: number; purchaseOrderProductId?: number } | null {
    if (!defect?.defectsFound?.length) {
      return null;
    }

    return purchaseOrderProductId
      ? defect.defectsFound.find((df): boolean => df.purchaseOrderProductId === purchaseOrderProductId)
      : defect.defectsFound.find((df): boolean => df.quantity === undefined || df.quantity === null || df.quantity === 0) ||
          defect.defectsFound[0];
  }

  private _hasInvalidQuantity(defectFound: { quantity?: number } | null): boolean {
    return defectFound != null && (defectFound.quantity === undefined || defectFound.quantity === null || defectFound.quantity === 0);
  }

  private _shouldSetDefaultQuantity(action: Action, workflow: IWorkflow): boolean {
    try {
      const defectInfo = this._extractDefectInfoFromAction(action);

      if (!defectInfo) {
        return false;
      }

      const defect = WorkflowUtils.getDefectById(workflow, defectInfo.defectId);
      const defectFound = this._findDefectFoundEntry(defect, defectInfo.purchaseOrderProductId);

      return this._hasInvalidQuantity(defectFound);
    } catch (error) {
      console.warn('Error checking defect quantity on defect interaction:', error);

      return false;
    }
  }

  private _extractDefectInfoForPictureMapping(
    action: {
      elementPathId: QimaOptionalType<string>;
      type: string;
    },
    workflow: IWorkflow
  ): {
    defectId: string;
    purchaseOrderProductId: QimaOptionalType<number>;
  } {
    const defectId = action.elementPathId!;
    const defect = WorkflowUtils.getDefectById(workflow, defectId);
    const defectFoundWithNullQuantity = defect.defectsFound.find(
      (df): boolean => df.quantity === undefined || df.quantity === null || df.quantity === 0
    );

    return {
      defectId,
      purchaseOrderProductId: defectFoundWithNullQuantity?.purchaseOrderProductId,
    };
  }

  private _createDefaultQuantityUpdateAction(action: Action, workflow: IWorkflow): Action {
    let defectId: string;
    let purchaseOrderProductId: QimaOptionalType<number>;

    if (action.type === UPDATE_DEFECT_COMMENT_SUCCESS) {
      const commentAction = action as IUpdateDefectCommentProps & { type: string };

      defectId = commentAction.defectId;
      purchaseOrderProductId = commentAction.purchaseOrderProductId;
    } else {
      const pictureAction = action as { elementPathId: QimaOptionalType<string>; type: string };
      const defectInfo = this._extractDefectInfoForPictureMapping(pictureAction, workflow);

      defectId = defectInfo.defectId;
      purchaseOrderProductId = defectInfo.purchaseOrderProductId;
    }

    const defect = WorkflowUtils.getDefectById(workflow, defectId);

    return updateDefect({
      defectId,
      quantity: 0,
      classification: defect.classification,
      purchaseOrderProductId,
    });
  }

  private _getDefectIdFromAction(action: Action): string | undefined {
    // Type guard for defect comment actions
    if (this._isDefectCommentAction(action)) {
      return action.defectId;
    }

    // Type guard for picture actions
    if (this._isPictureAction(action)) {
      return action.elementPathId || undefined;
    }

    return undefined;
  }

  private _isDefectCommentAction(action: Action): action is IUpdateDefectCommentProps & { type: string } {
    return action.type === UPDATE_DEFECT_COMMENT_SUCCESS && 'defectId' in action;
  }

  private _isPictureAction(action: Action): action is { elementPathId: string; type: string } {
    return action.type === ADD_INSPECTION_PICTURE_SUCCESS && 'elementPathId' in action;
  }
}
